import { forwardRef } from 'react';
import classNames from 'classnames';
import Info from '../Info/Info';
import FormInputProps from './FormInputProps';
import styles from './FormInput.module.scss';

export interface FormInputFieldProps extends Omit<FormInputProps, 'control' | 'suffix' | 'prefix' | 'precision'> {
  value?: string | number;
  error?: string;
}

// The system input presentation, shared by React Hook Form and controlled forms.
const FormInputField = forwardRef<HTMLInputElement | HTMLTextAreaElement, FormInputFieldProps>(({
  id, name, type = 'text', as = 'input', label, ariaLabel, required = false,
  info, infoAriaLabel, infoClasses, placeholder, classes, isDisabled, readonly,
  onChange, onBlur, onEnter, autoComplete = 'true', value = '', error, min, max,
}, ref) => {
  const Tag = as;
  return (
    <div className={classNames('input', classes)} data-status={error ? 'error' : ''}>
      <label className="input__label" htmlFor={id}>
        {label && <span className="input__label-text">
          {label} {required && <span aria-hidden="true">*</span>}
          {!!info && <Info tooltipId={name + id} ariaLabel={infoAriaLabel}
            classes={classNames('input__info', infoClasses)}>{info}</Info>}
        </span>}
        <Tag
          ref={(element: HTMLInputElement | HTMLTextAreaElement | null) => {
            if (typeof ref === 'function') ref(element);
            else if (ref) ref.current = element;
          }}
          id={id}
          name={name}
          aria-label={ariaLabel}
          aria-required={required || undefined}
          aria-invalid={!!error}
          readOnly={readonly}
          className={classNames('input__field', styles.input)}
          type={as === 'textarea' ? undefined : type}
          style={{ height: type === 'color' ? 50 : 'auto', padding: type === 'color' ? '0 20px' : undefined }}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={(event) => { if (event.key === 'Enter' && onEnter) onEnter(event); }}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={isDisabled}
          value={value}
          min={min}
          max={max}
        />
        {!!error && <span className={styles.error}>
          <Info place="left" tooltipId={'error' + name + id} ariaLabel={`${label ?? name}: ${error}`}>
            {error}
          </Info>
        </span>}
      </label>
    </div>
  );
});

FormInputField.displayName = 'FormInputField';
export default FormInputField;
